import rootStore from 'stores/index';
import log from 'services/helpers/log';
import { IGetDataParams } from '../../types';
import { getConditions } from 'stores/_services/filterService';
import { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';

const TOKEN_KEY = 'auth-token';
const API_ROUTE = '/api';
//Routes for which order by id need to be excluded
const EXCLUDE_ROUTES = ['users', 'useractivities/activity'];

export enum EntityName {
    AGENT_DETAILS = 'agentDetails',
    LOCATION_DETAILS = 'locationDetails',
    ORGANIZATION_DETAILS = 'organizationDetails',
    PERSONAL_ACCESS_TOKENS = 'personalAccessTokens',
    USERS_ME_PERSONAL_ACCESS_TOKENS = 'users/me/personalAccessTokens',
    POLICIES = 'policies',
    SETTINGS = 'settings',
    EXTENSION_RESPONSE_DETAILS = 'extensionResponseDetails',
    EXTENSION_RESPONSE_QUEUE_DETAILS = 'extensionResponseQueueDetails',
    USER_ACTIVITIES = 'useractivities',
    USER_TASKS = 'userTasks',
    USER_TASKS_ITEMS = 'userTaskItems',
    EXTENSIONS = 'extensions',
    EXTENSIONS_GLOBAL_VARIABLES = 'extensionGlobalVariables'
}

export function getDefaultHeaders() {
    return {
        pragma: 'no-cache',
        Authorization: getToken() || ''
    };
}

export function getUrl(route, options?) {
    if (route.startsWith('/')) {
        route = route.slice(1);
    }

    options = options ? buildOptions(options) : '';

    return `${API_ROUTE}/${route}${options}`;
}

export async function post(route, payload, options?) {
    try {
        const response = await fetch(getUrl(route, options), {
            method: 'POST',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/json'
            },
            body: payload ? JSON.stringify(payload) : undefined
        });

        const data = await getResponseContent(route, response);
        return response.ok ? handleSuccess({ response, data }) : handleError({ response, data, error: new Error(response.statusText) });
    } catch (e) {
        return handleError({ response: null, data: null, error: e });
    }
}

export async function postFile(route, payload, contentType) {
    if (!payload || !contentType) {
        throw new Error();
    }

    try {
        const response = await fetch(getUrl(route), {
            method: 'POST',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': contentType
            },
            body: payload
        });

        const data = await getResponseContent(route, response);

        return response.ok ? handleSuccess({ response, data }) : handleError({ response, data, error: new Error(response.statusText) });
    } catch (e) {
        return handleError({ response: null, data: null, error: e });
    }
}

export async function put(route, payload, options) {
    try {
        const response = await fetch(getUrl(route, options), {
            method: 'PUT',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/json'
            },
            body: payload ? JSON.stringify(payload) : undefined
        });

        const data = await getResponseContent(route, response);
        return response.ok ? handleSuccess({ response, data }) : handleError({ response, data, error: new Error(response.statusText) });
    } catch (e) {
        return handleError({ response: null, data: null, error: e });
    }
}

export async function patch(route, payload) {
    try {
        const response = await fetch(getUrl(route), {
            method: 'PATCH',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        const data = await getResponseContent(route, response);
        return response.ok ? handleSuccess({ response, data }) : handleError({ response, data, error: new Error(response.statusText) });
    } catch (e) {
        return handleError({ response: null, data: null, error: e });
    }
}

export async function destroy(route, where) {
    const options = { queryParams: { where: formatWhere(where) } };

    try {
        const response = await fetch(getUrl(route, options), {
            method: 'DELETE',
            headers: getDefaultHeaders()
        });

        const data = await getResponseContent(route, response);
        return response.ok ? handleSuccess({ response, data }) : handleError({ response, data, error: new Error(response.statusText) });
    } catch (e) {
        return handleError({ response: null, data: null, error: e });
    }
}

export async function get<T>(route: string, options: Record<string, any> = {}): Promise<{ data: T; response: Response } | void> {
    rootStore.loadingStore.startLoading(route);

    try {
        const response = await fetch(getUrl(route, options), {
            method: 'GET',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/json',
                Accept: 'application/json'
            } as HeadersInit
        });

        const data = await getResponseContent(route, response);
        return response.ok ? handleSuccess({ response, data }) : handleError({ response, data, error: new Error(response.statusText) });
    } catch (e) {
        return handleError({ response: null, data: null, error: e });
    }
}

export async function head(route) {
    try {
        const response = await fetch(getUrl(route), {
            method: 'HEAD',
            headers: {
                ...getDefaultHeaders()
            }
        });

        return response.status === 200;
    } catch (e) {
        return handleError({ response: null, data: null, error: e });
    }
}

// TODO: remove string type from route in the future
export async function getPage(route: EntityName | string, options: Record<string, any> = {}) {
    options.queryParams = options.queryParams || {};

    if (!('count' in options.queryParams) && options.count) {
        options.queryParams.count = true;
    }

    if (!options.queryParams.count) {
        delete options.queryParams.count;
    }

    if (!EXCLUDE_ROUTES.includes(route) && options.filter && options.filter.order) {
        if (!Array.isArray(options.filter.order)) {
            options.filter.order = [options.filter.order, 'id ASC'];
        } else {
            options.filter.order.push('id ASC');
        }
    }

    const { data, response } = await get(route, options);

    return {
        data,
        count: parseInt(response.headers.get('X-Total-Count') || 0)
    };
}

// TODO: remove string type from route in the future
export async function getCount(route: EntityName | string, where?: Record<string, unknown>) {
    const options = { queryParams: { where: formatWhere(where) } };
    const { data } = await get(`${route}/count`, options);
    return data.count;
}

export async function findOne(route, options) {
    const { filter = {} } = options;
    filter.limit = 1;
    return await get(route, { filter });
}

export function getToken() {
    return sessionStorage.getItem(TOKEN_KEY);
}

export function buildQuery(queryParams = {}) {
    return Object.keys(queryParams)
        .filter((k) => {
            if (queryParams[k] && typeof queryParams[k] === 'object') {
                return !!Object.values(queryParams[k]).length;
            }

            return queryParams[k] != null;
        })
        .map((k) => {
            let value = queryParams[k];
            if (queryParams && typeof queryParams[k] === 'object') {
                value = JSON.stringify(queryParams[k]);
            }

            return `${encodeURIComponent(k)}=${encodeURIComponent(value)}`;
        })
        .join('&');
}

export function buildWhereFilter(options) {
    return formatWhere(options);
}

export function formatWhere(options) {
    if (!(options && options.where)) return;

    if (!Array.isArray(options.where)) {
        if (typeof options.where !== 'object') throw new Error("dataService: expected argument 'where' to be an array or an object.");

        options.where = [options.where];
    }

    const conditions = options.where
        .map((condition) => {
            if (condition && typeof condition === 'object' && 'search' in condition) {
                if (!condition.search) return; // remove empty searches just in case

                if (!options.searchFields) throw new Error('dataService: search condition requires searchFields option');

                if (!Array.isArray(options.searchFields)) options.searchFields = [options.searchFields];

                const fields = options.searchFields.map((field) => {
                    const searchField = {};

                    // Manually escape backslashes so loopback doesn't blow up
                    const searchValue = condition.search.replace(/([{}()+^$\\])/g, '\\$1');
                    //field ip is of type inet
                    const filteredFields = ['ip'];

                    if (filteredFields.includes(field)) {
                        searchField[field] = { ilike: `%${searchValue}%` };
                    } else {
                        searchField[field] = { regexp: `/${searchValue}/i` };
                    }
                    return searchField;
                });

                return { or: fields };
            }

            return condition;
        })
        .filter((condition) => condition);

    if (conditions.length) return { and: conditions };
}

export function buildOptions(options) {
    let queryString = '';

    if (options) {
        options.queryParams = options.queryParams || {};

        if (options.filter) {
            // remove searchFields key from filter sent to API
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { searchFields, includeFields, excludeFields, ...filter } = options.filter;

            let fields;

            if (includeFields) {
                fields = getFields(includeFields);
            } else if (excludeFields) {
                fields = getFields(excludeFields, false);
            }

            options.queryParams.filter = {
                ...filter,
                where: formatWhere(options.filter),
                fields,
                ...options.queryParams.filter
            };
        }

        if (options.queryParams) {
            queryString = buildQuery(options.queryParams);
            queryString = queryString.length ? `?${queryString}` : '';
        }
    }

    return queryString;
}

export function getFields(input, include = true) {
    if (!Array.isArray(input)) {
        if (typeof input == 'string') input = [input];
        else throw new Error('fields must be a string or an array of strings');
    }

    const fields = {};

    input.forEach((field) => (fields[field] = include));

    return fields;
}

export async function handleSuccess({ response, data }) {
    switch (response.status) {
        case 200:
            return {
                response,
                data
            };
        case 204:
        default:
            return response;
    }
}

export async function handleError({ response, data, error }) {
    if (error?.name === 'NetworkError') {
        throw error;
    }

    switch (response.status) {
        case 417:
            window.location = '/license-agreement';
            break;
        default:
            let err: any;

            if (error) {
                err = error;
            } else {
                err = new Error(response.statusText);

                try {
                    const result = data;

                    if (result && result.error) {
                        err = new Error(result.error.message || result.error);
                    }
                } catch (e) {
                    log.error(e);
                }

                err.status = err.status || response.status;
                throw err;
            }

            log.error(err);
            throw err;
    }
}

export async function getResponseContent(route, response) {
    const totalLength = +response.headers.get('content-length');
    const reader = response.body.getReader();
    let receivedLength = 0;
    const chunks = [];

    rootStore.loadingStore.updateLoadingProgress(route, receivedLength, totalLength);

    while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        rootStore.loadingStore.updateLoadingProgress(route, receivedLength, totalLength);
    }

    rootStore.loadingStore.stopLoading(route);

    const allChunks = new Uint8Array(receivedLength);
    let position = 0;
    for (const chunk of chunks) {
        allChunks.set(chunk, position);
        position += chunk.length;
    }

    let content = new TextDecoder('utf-8').decode(allChunks);

    if (content) {
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
            content = JSON.parse(content);
        }

        return content;
    }
}

export async function getDetailsForCorrectPagination<T>({
    params,
    entityName
}: {
    params: IGetDataParams & {
        searchFields?: string[];
        include?: Record<string, any>[];
        includeFields?: string[];
    };
    entityName: EntityName;
}): Promise<{
    data: T[];
    totalCount: number;
}> {
    const { filter, sortBy, sortDirection, pageSize, page, searchFields, include, includeFields } = params;

    const conditions = getConditions(filter);
    const order = `${sortBy} ${sortDirection}`;
    const limit = pageSize || DEFAULT_LIMIT;
    const skip = ((page || DEFAULT_PAGE_NUMBER) - 1) * limit;

    const options = {
        filter: {
            searchFields,
            order,
            limit,
            skip,
            where: conditions,
            include,
            includeFields
        }
    };

    const [{ data }, totalCount] = await Promise.all([getPage(entityName, options), getCount(entityName, { where: conditions, searchFields })]);

    return { data, totalCount };
}

export async function deleteById(route: string): Promise<void> {
    try {
        const response = await fetch(getUrl(route), {
            method: 'DELETE',
            headers: getDefaultHeaders()
        });

        if (!response.ok) {
            throw new Error(response.statusText);
        }
    } catch (e) {
        throw e;
    }
}
